import React, { useState, useEffect, useCallback } from "react";
import { useHistory } from "react-router-dom";
import { Layout, Row, Col, Form, Spin, message, Button, Tooltip } from "antd";
import { LoadingOutlined } from "@ant-design/icons";
import authorization from "../../../auth/authorization";
import { lookUp } from "../../../api/LookUpAPI";
import { getCasesV2, exportCEVData } from "../../../api/CasesAPI";
import { useTablePreferences } from "../../../hooks/useTablePreferences";
import BulkUpdateModal from '../../../components/BulkUpdate/BulkUpdateModal'
import UserRolesAndRightsHOComponent from "../../../components/UserRolesAndRightsHOComponent/UserRolesAndRightsHOComponent";
import { CaseFilterMap } from "../../../constants/CaseFilter"
import { LocalStorageEnum } from "../../../constants/enums";
import { createTableQueryParamMap, createTableSortByMap } from "../../../utils/createCaseFilterQueryParamMap";
import { dateTimeFormatter } from "../../../utils/dateFormatter";
import CaseFilter from "./CaseFilter";
import { StyledTabWrapper, StyledComponentWrapper, StyledTable, Title } from "./StyledComponents";
import SelectBulkAction from "./SelectBulkAction";
import { useColumns } from "./hooks/useColumns";
import { useLocalStorage } from '../../../hooks/useLocalStorage';

const { Content } = Layout;

const TABLE_NAME = "manage-cases";

const getFetchedDataHandler = (setter) => (res) => {
  if (!res.data?.payload) {
    throw Error();
  }
  const fetchedData = res.data.payload.map((item) => ({ ...item, key: item._id }));
  setter(fetchedData);
};

const TableDataMap = {
  CASE_ID: CaseFilterMap.CASE_ID,
  TELEMEDICINE_CATEGORY_ID: CaseFilterMap.TELEMEDICINE_CATEGORY_ID,
  PHYSICIAN_ID: CaseFilterMap.PHYSICIAN_ID,
  PATIENT_NAME: CaseFilterMap.PATIENT_NAME,
  PATIENT_EMAIL: 'patientEmail',
  PATIENT_ID: 'patientId',
  STATE_ID: CaseFilterMap.STATE_ID,
  CREATED_DATE: CaseFilterMap.CREATED_DATE,
  UPDATED_DATE: CaseFilterMap.UPDATED_DATE,
  CONSULTATION_TYPE: CaseFilterMap.TYPE,
  STATUS: CaseFilterMap.STATUS,
  ASSIGNED_TIME: 'assignedTime',
  ASSIGNED_GROUP: 'assignedGroup',
  ASSIGNED_GROUP_TIME: 'assignedGroupTime',
}

const TableSortByMap = createTableSortByMap(TableDataMap);

const defaultPreferences = { currentPage: 1, tablePageSize: 100, sortOrder: 'descend' }

const ManageCases = ({ userType }) => {
  const history = useHistory();
  const [loading, setLoading] = useState(false);
  const [cases, setCases] = useState([]);
  const [clients, setClients] = useState([]);
  const [states, setStates] = useState([]);
  const [telemedicineCategory, setTelemedicineCategory] = useState([]);
  const [caseStatus, setCaseStatus] = useState([]);
  const [totalCase, setTotalCase] = useState(0);
  const [bulkAction, setBulkAction] = useState(null)
  const [selectedCases, setSelectedCases] = useState([])
  const [searchFilter, setSearchFilter] = useState({});
  const [filterForm] = Form.useForm();

  const [_, setCaseFilters] = useLocalStorage(LocalStorageEnum.CASE_FILTERS, {});
  const [__, setCaseSearchResults] = useLocalStorage(LocalStorageEnum.CASE_SEARCH_RESULTS, []);

  const { tablePreferences, getTablePreferences, updateTablePreferences } = useTablePreferences();
  const { currentPage, tablePageSize, sortOrder, sortBy } = getTablePreferences(TABLE_NAME, defaultPreferences);

  const checkIsAllowedAuthUser = useCallback((action) => {
    return authorization.isAllowed(userType, action);
  }, [userType]);

  const columns = useColumns({ sortOrder, sortBy, TableDataMap, userType });

  const mapCasesForTable = useCallback((cases = []) => {
    return cases.map((c) => {
      return {
        key: c.caseId,
        rowKey: c.caseId,
        [TableDataMap.CASE_ID]: { caseId: c.caseId, colourCode: c.colourCode },
        [TableDataMap.TELEMEDICINE_CATEGORY_ID]: c.categoryName,
        [TableDataMap.PHYSICIAN_ID]: c.physicianName,
        [TableDataMap.PATIENT_NAME]: c.patientName,
        [TableDataMap.PATIENT_EMAIL]: c.patientEmail,
        [TableDataMap.PATIENT_ID]: c.patientId,
        [TableDataMap.STATE_ID]: c.state,
        [TableDataMap.CREATED_DATE]: dateTimeFormatter(c.createdDate),
        [TableDataMap.UPDATED_DATE]: dateTimeFormatter(c.updatedDate),
        [TableDataMap.CONSULTATION_TYPE]: c.consultationType,
        [TableDataMap.STATUS]: c.status,
      }
    })
  }, [])

  const getCasesByParams = useCallback((paramMap) => {
    setLoading(true);
    setSelectedCases([]);
    getCasesV2(paramMap)
      .then(({ data: caseData }) => {
        const payload = caseData?.payload;
        
        if (!payload.data) {
          throw Error();
        }

        const { data, count } = payload;

        const mappedCases = mapCasesForTable(data);

        setTotalCase(count);
        setCases(mappedCases);
        setCaseSearchResults(mappedCases);
        message.success("Successfully loaded Case List");
      })
      .catch((error) => {
        console.log("Error fetching cases", error);
        message.error("Unable to load Case List. Please try again!");
      })
      .finally(() => setLoading(false));
  }, [mapCasesForTable]);

  const fetchCases = useCallback((
    tablePreferences = {
      currentPage,
      tablePageSize,
      sortOrder,
      sortBy: TableSortByMap[sortBy] || sortBy,
    }
  ) => {

    const hasFilterValues = Object.values(searchFilter).some((param) => {
      return Array.isArray(param) ? param.length > 0 : !!param
    });

    if (!hasFilterValues) {
      return;
    }

    updateTablePreferences(TABLE_NAME, tablePreferences);
    const tableFilterMap = createTableQueryParamMap(tablePreferences);
    getCasesByParams({ ...tableFilterMap, ...searchFilter })
  }, [searchFilter, currentPage, tablePageSize, sortOrder, sortBy, getCasesByParams]);

  const onFilter = useCallback((params) => {
    // persist case filters
    setCaseFilters(params);

    updateTablePreferences(TABLE_NAME, { currentPage: 1 });
    setSearchFilter(params);
  }, [updateTablePreferences]);

  const clearFilter = useCallback(() => {
    filterForm.resetFields();

    updateTablePreferences(TABLE_NAME, { currentPage: 1 });
    setCaseFilters({});
    setCaseSearchResults([]);

    setSelectedCases([]);
    setCases([]);
    setSearchFilter({});
  }, [filterForm, updateTablePreferences]);

  const hasCEVFeedbackTag = useCallback(() => {
    const tags = searchFilter?.tags;
    return tags && Array.isArray(tags) && tags.some(t => t.startsWith('*CEV_FEEDBACK'));
  }, [searchFilter]);

  const handleExportCEV = useCallback(async () => {
    const hasFilterValues = Object.values(searchFilter).some((param) => {
      return Array.isArray(param) ? param.length > 0 : !!param
    });

    if (!hasFilterValues) {
      message.warning("Please apply filters before exporting");
      return;
    }

    if (!hasCEVFeedbackTag()) {
      message.warning("Please include the *CEV_FEEDBACK tag in your filters");
      return;
    }

    setLoading(true);
    try {
      const { data } = await exportCEVData(searchFilter);
      
      if (!data?.data?.length) {
        message.warning("No data to export");
        return;
      }

      // Convert to CSV
      const csvHeaders = ['Case ID', 'Category', 'MIF', 'CEV Feedback'];
      const csvRows = data.data.map(row => [
        row.caseId,
        `"${(row.categoryName || '').replace(/"/g, '""')}"`,
        `"${(row.mif || '').replace(/"/g, '""')}"`,
        `"${(row.cevFeedback || '').replace(/"/g, '""')}"`
      ]);
      
      const csvContent = [
        csvHeaders.join(','),
        ...csvRows.map(row => row.join(','))
      ].join('\n');

      // Create download
      const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
      const link = document.createElement('a');
      const url = URL.createObjectURL(blob);
      link.setAttribute('href', url);
      link.setAttribute('download', `cev-export-${new Date().toISOString().split('T')[0]}.csv`);
      link.style.visibility = 'hidden';
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);

      message.success(`Successfully exported ${data.data.length} cases`);
    } catch (error) {
      console.error("Error exporting CEV data", error);
      message.error("Failed to export CEV data. Please try again.");
    } finally {
      setLoading(false);
    }
  }, [searchFilter, hasCEVFeedbackTag]);

  const onTableChange = (pagination, _filters, sorter) => {
    const { current, pageSize } = pagination;
    const { order, field } = sorter

    const newTablePreferences = {
      sortOrder: order || 'ascend',
      sortBy: field,
      tablePageSize: pageSize,
      currentPage: current,
    }

    if (pageSize !== tablePreferences[TABLE_NAME].tablePageSize) {
      newTablePreferences.currentPage = 1
    }
    
    fetchCases(newTablePreferences);
  }

  // fetch filter options
  useEffect(() => {
    const fetchDataMap = {
      "case-status": setCaseStatus,
      "state": setStates,
      "client": setClients,
      "tele-medicine-category": setTelemedicineCategory,
    }

    Object.entries(fetchDataMap).forEach(([key, setter]) => {
      lookUp(key).then(getFetchedDataHandler(setter)).catch(() => message.error(`Unable to fetch ${key}`));
    });
  }, []);

  useEffect(() => {
    fetchCases();
  }, [searchFilter, fetchCases]);

  return (
    <>
      <Content>
        <StyledTabWrapper>
          <StyledComponentWrapper>
                <Spin
                  spinning={loading}
                  size="large"
                  indicator={<LoadingOutlined />}
                >
                  <Row justify="space-between" align="middle" style={{ marginBottom: 16 }}>
                    <Col xs={24} sm={24} md={12} lg={12} xl={12}>
                      <Title level={2} style={{ margin: 0 }}>All Cases</Title>
                    </Col>
                    <Col xs={24} sm={24} md={12} lg={12} xl={12} style={{ textAlign: 'right' }}>
                      <Tooltip 
                        title={
                          !cases.length 
                            ? "No cases to export" 
                            : !hasCEVFeedbackTag() 
                            ? "Please include *CEV_FEEDBACK tag in filters to export" 
                            : ""
                        }
                      >
                        <Button 
                          id="manage-cases-export-cev-btn"
                          type="primary" 
                          onClick={handleExportCEV}
                          disabled={!cases.length || !hasCEVFeedbackTag()}
                        >
                          Export CEV Data
                        </Button>
                      </Tooltip>
                    </Col>
                  </Row>
                  <CaseFilter
                    clients={clients}
                    states={states}
                    telemedicineCategory={telemedicineCategory}
                    caseStatus={caseStatus}
                    onFilter={onFilter}
                    clearFilter={clearFilter}
                    onSearchCases={fetchCases}
                    form={filterForm}
                    setCases={setCases}
                    setSearchFilter={setSearchFilter}
                  />
                  <Row>
                    <SelectBulkAction
                      disabled={!selectedCases?.length}
                      onChange={setBulkAction}
                      checkIsAllowedAuthUser={checkIsAllowedAuthUser}
                      isArchived={Boolean(filterForm.getFieldValue("isArchived"))}
                      isDeleted={Boolean(filterForm.getFieldValue("isDeleted"))}
                    />
                  </Row>
                  <Row>
                    <Col span={24}>
                      <StyledTable
                        columns={columns}
                        dataSource={cases}
                        onChange={onTableChange}
                        rowSelection={{
                          selectedRowKeys: selectedCases.map(_case => _case[TableDataMap.CASE_ID]),
                          onChange: (_, selectedRows) => setSelectedCases(selectedRows.map(_case => {
                            return { ..._case, [TableDataMap.CASE_ID]: _case[TableDataMap.CASE_ID].caseId };
                          })),
                        }}
                        onRow={(record) => ({
                          onClick: () => history.push(`/cases/${record[TableDataMap.CASE_ID].caseId}`)
                        })}
                        pagination={{
                          total: totalCase,
                          showTotal: (totalCase) => `Total ${totalCase} Cases`,
                          current: currentPage,
                          pageSize: tablePageSize,
                          pageSizeOptions: ["5", "10", "20", "50", "100", "500"],
                          showSizeChanger: true,
                        }}
                        scroll={{ x: 720 }}
                      />
                    </Col>
                  </Row>
                </Spin>
              </StyledComponentWrapper>
        </StyledTabWrapper>
        {bulkAction && (
          <BulkUpdateModal
            selectedCases={selectedCases}
            setSelectedCases={setSelectedCases}
            bulkAction={bulkAction}
            setBulkAction={setBulkAction}
            fetchCases={fetchCases}
          />
        )}
      </Content>
    </>
  );
};

export default UserRolesAndRightsHOComponent(ManageCases);
